import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Track, TrackCues } from '@app/core/models/Track';
import { HotkeysService } from '@app/core/services/hotkeys.service';
import { AuthState } from '@app/core/states/auth.state';
import { RadioState } from '@app/core/states/radio.state';
import { msToStringValue } from '@app/library/components/modals/cue-input/cue-input.component';
import {
  CalculateTrackCuesFailure,
  CalculateTrackCuesRequest,
  CalculateTrackCuesSuccess,
  DeleteNextTrackCues,
  DeletePreviousTrackCues,
  NextTrackCuesRequest,
  PreviousTrackCuesRequest,
  SaveNextTrack,
  SavePreviousTrack,
  TrackCuesFailure,
  TrackCuesRequest,
  TrackCuesSuccess,
  UpdateTrackCuesFailure,
  UpdateTrackCuesRequest,
  UpdateTrackCuesSuccess,
} from '@app/library/states/track-mix-points.actions';
import { TrackMixPointsState } from '@app/library/states/track-mix-points.state';
import { TranslateService } from '@ngx-translate/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { WaveformUiComponent } from '@radioking/library/waveform/angular';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import amplitude from 'amplitude-js';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, mergeMap } from 'rxjs/operators';

interface DataInput {
  track: Track;
}

@Component({
  selector: 'rk-mix-points-modal',
  templateUrl: './mix-points-modal.component.html',
  styleUrls: ['./mix-points-modal.component.scss'],
})
export class MixPointsModalComponent implements OnInit, OnDestroy {
  @ViewChild(WaveformUiComponent)
  waveform: WaveformUiComponent;

  isLoading$: Observable<boolean>;
  isSaving$: Observable<boolean>;
  isCalculating$: Observable<boolean>;
  previousTrack$: Observable<Track>;
  nextTrack$: Observable<Track>;

  formCues: UntypedFormGroup;

  @Select(TrackMixPointsState.previousCues)
  previousTrackCues$: Observable<TrackCues>;

  @Select(TrackMixPointsState.nextCues)
  nextTrackCues$: Observable<TrackCues>;

  waveformPlaying = false;
  subscription = new Subscription();
  calculateLabel: string;
  trackDuration: number;

  constructor(
    public readonly dialogRef: MatDialogRef<MixPointsModalComponent>,
    private readonly store: Store,
    private readonly actions$: Actions,
    private readonly fb: UntypedFormBuilder,
    private readonly uiStore: UiStoreHelperService,
    private readonly translate: TranslateService,
    private readonly hotkeys: HotkeysService,
    @Inject(MAT_DIALOG_DATA) public dataIn: DataInput,
  ) {}

  ngOnInit() {
    amplitude.getInstance().logEvent('edit mixpoints form');
    this.isLoading$ = this.uiStore.getLoadingState(
      TrackCuesRequest,
      [TrackCuesSuccess, TrackCuesFailure],
      true,
    );
    this.isSaving$ = this.uiStore.getLoadingState(UpdateTrackCuesRequest, [
      UpdateTrackCuesSuccess,
      UpdateTrackCuesFailure,
    ]);
    this.isCalculating$ = this.uiStore.getLoadingState(CalculateTrackCuesRequest, [
      CalculateTrackCuesSuccess,
      CalculateTrackCuesFailure,
    ]);

    const radioId = this.store.selectSnapshot(RadioState.currentRadioId);

    this.previousTrack$ = this.store
      .select(TrackMixPointsState.previousTrack)
      .pipe(map(fn => fn(radioId)));
    const previousTrackSubscription = this.previousTrack$
      .pipe(distinctUntilChanged())
      .subscribe(track => {
        if (track) {
          this.store.dispatch(new PreviousTrackCuesRequest(track));
        } else {
          this.store.dispatch(new DeletePreviousTrackCues());
        }
      });
    this.subscription.add(previousTrackSubscription);

    this.nextTrack$ = this.store
      .select(TrackMixPointsState.nextTrack)
      .pipe(map(fn => fn(radioId)));
    const nextTrackSubscription = this.nextTrack$
      .pipe(distinctUntilChanged())
      .subscribe(track => {
        if (track) {
          this.store.dispatch(new NextTrackCuesRequest(track));
        } else {
          this.store.dispatch(new DeleteNextTrackCues());
        }
      });
    this.subscription.add(nextTrackSubscription);

    this.subscription.add(
      this.isCalculating$.subscribe(val => {
        this.calculateLabel = !val
          ? this.translate.instant('MEDIA.MODAL.TRACK.MIX_POINTS.AUTO')
          : this.translate.instant('MEDIA.MODAL.TRACK.MIX_POINTS.AUTO_PROCESSING');
      }),
    );

    const datas$ = combineLatest([this.previousTrackCues$, this.nextTrackCues$]).pipe(
      mergeMap(() => this.actions$.pipe(ofActionDispatched(TrackCuesSuccess))),
    );
    this.subscription.add(
      datas$.subscribe(({ cues }) => {
        this.createForm(cues);
      }),
    );

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(CalculateTrackCuesSuccess))
        .subscribe(({ cues }) => {
          // Clear mix points to avoid wrong computations (incompatibility/overlap between old and new values)
          this.removeFadeIn(true);
          this.removeFadeOut();
          this.removeMix();

          // Apply new mix points
          this.startChanged(cues.intro / 1000);
          this.fadeInChanged(cues.fadeIn / 1000);
          this.endChanged(cues.extro / 1000);
          this.fadeOutChanged(cues.fadeOut / 1000);
          this.mixChanged(cues.mix / 1000);
        }),
    );

    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(UpdateTrackCuesSuccess))
        .subscribe(() => this.dialogRef.close()),
    );

    // Hotkeys
    this.subscription.add(
      this.hotkeys.fromConfigArray([
        { hotkey: 'playPause', handler: () => this.onPlayPauseHotkey() },
        { hotkey: 'confirm', handler: () => this.onConfirmHotkey() },
      ]),
    );
  }

  onPlayPauseHotkey() {
    this.playOrPause();
  }

  onConfirmHotkey() {
    this.onSubmit();
  }

  @Dispatch()
  blurPreviousTrackInput(): SavePreviousTrack {
    return new SavePreviousTrack(null);
  }

  @Dispatch()
  blurNextTrackInput(): SaveNextTrack {
    return new SaveNextTrack(null);
  }

  @Dispatch()
  savePreviousTrack(val: Track): SavePreviousTrack {
    return new SavePreviousTrack(val);
  }

  @Dispatch()
  saveNextTrack(val: Track): SaveNextTrack {
    return new SaveNextTrack(val);
  }

  @Dispatch()
  calculateTrackCues(): CalculateTrackCuesRequest {
    return new CalculateTrackCuesRequest(this.dataIn.track);
  }

  startChanged(e: number) {
    if (typeof e === 'number') {
      this.formCues.get('intro').setValue(e * 1000);
    }
  }
  fadeInChanged(e: number) {
    if (typeof e === 'number') {
      this.formCues.get('fadeIn').setValue(e * 1000);
      if (e === 0) {
        this.removeFadeIn();
      }
    }
  }
  mixChanged(e: number) {
    if (typeof e === 'number') {
      this.formCues.get('mix').setValue(e * 1000);
    }
  }
  fadeOutChanged(e: number) {
    if (typeof e === 'number') {
      this.formCues.get('fadeOut').setValue(e * 1000);
    }
  }
  endChanged(e: number) {
    if (typeof e === 'number') {
      this.formCues.get('extro').setValue(e * 1000);
    }
  }
  isPlaying(e: boolean) {
    if (typeof e === 'boolean') {
      this.waveformPlaying = e;
    }
  }
  durationChanged(e: number) {
    if (typeof e === 'number') {
      this.trackDuration = e;
    }
  }

  setStartAtCursor() {
    this.waveform.setStartAtCursor();
  }
  resetStart() {
    this.waveform.resetStartOfTrack();
  }

  setFadeInAtCursor() {
    this.waveform.setFadeInAtCursor();
  }
  removeFadeIn(force = false) {
    if (force || this.formCues.get('fadeIn')?.value !== 0) {
      this.waveform.removeFadeIn();
    }
  }

  setFadeOutAtCursor() {
    this.waveform.setFadeOutAtCursor();
  }
  removeFadeOut() {
    this.waveform.removeFadeOut();
  }

  setMixAtCursor() {
    this.waveform.setMixAtCursor();
  }
  removeMix() {
    this.waveform.removeMix();
  }

  setEndAtCursor() {
    this.waveform.setEndAtCursor();
  }
  resetEnd() {
    this.waveform.resetEndOfTrack();
  }

  trackUrl() {
    const authToken = this.store.selectSnapshot(AuthState.getAccessToken);

    return `${this.dataIn.track.file}?token=${authToken}`;
  }

  createForm(cues: TrackCues) {
    this.formCues = this.fb.group({
      intro: cues.intro,
      fadeIn: cues.fadeIn,
      mix: cues.mix,
      fadeOut: cues.fadeOut,
      extro: cues.extro,
      waveformUrl: cues.waveformUrl,
    });
    this.formCues.get('intro').valueChanges.subscribe(val => {
      this.waveform.setStart(val / 1000);
    });
    this.formCues.get('fadeIn').valueChanges.subscribe(val => {
      if (val !== 0) {
        this.waveform.setFadeIn(val / 1000);
      }
    });
    this.formCues.get('mix').valueChanges.subscribe(val => {
      if (val !== 0) {
        this.waveform.setMix(val / 1000);
      }
    });
    this.formCues.get('fadeOut').valueChanges.subscribe(val => {
      if (val !== 0) {
        this.waveform.setFadeOut(val / 1000);
      }
    });
    this.formCues.get('extro').valueChanges.subscribe(val => {
      this.waveform.setEnd(val / 1000);
    });
  }

  @Dispatch()
  onSubmit = () => {
    const formValues = this.formCues.value;

    return new UpdateTrackCuesRequest(this.dataIn.track, formValues);
  };

  onCancel() {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  playIntro(): void {
    this.waveform.playIntro();
  }

  playOrPause(): void {
    this.waveform.playOrPause();
  }

  playEnd(): void {
    this.waveform.playEnd();
  }

  msToStringValue(ms: number): string {
    return msToStringValue(ms);
  }

  getMinValueForField(field: string): string {
    if (field === 'fadeOut' || field === 'mix') {
      const values = [
        this.formCues.get('intro')?.value || 0,
        this.formCues.get('fadeIn')?.value || 0,
      ];

      return msToStringValue(
        values.filter(x => x !== 0).reduce((p, n) => Math.max(p, n), 0),
      );
    }
    if (field === 'extro') {
      const values = [
        this.formCues.get('intro')?.value || 0,
        this.formCues.get('fadeIn')?.value || 0,
        this.formCues.get('fadeOut')?.value || 0,
        this.formCues.get('mix')?.value || 0,
      ];

      return msToStringValue(
        values.filter(x => x !== 0).reduce((p, n) => Math.max(p, n), 0),
      );
    }

    return msToStringValue(0);
  }

  getMaxValueForField(field: string): string {
    if (field === 'intro') {
      const values = [
        this.formCues.get('fadeIn')?.value || 0,
        this.formCues.get('fadeOut')?.value || 0,
        this.formCues.get('mix')?.value || 0,
        this.formCues.get('extro')?.value || 0,
      ];

      return msToStringValue(
        values.filter(x => x !== 0).reduce((p, n) => Math.min(p, n), Infinity),
      );
    }
    if (field === 'fadeIn') {
      const values = [
        this.formCues.get('fadeOut')?.value || 0,
        this.formCues.get('mix')?.value || 0,
        this.formCues.get('extro')?.value || 0,
      ];

      return msToStringValue(
        values.filter(x => x !== 0).reduce((p, n) => Math.min(p, n), Infinity),
      );
    }

    return msToStringValue(this.trackDuration * 1000);
  }

  getWindowHeight(): number {
    return window.innerHeight;
  }
}
